(function() {
  'use strict';

  // Gallery thumbnail navigation - properly synced with main slider
  function initGalleryThumbnails() {
    // Initialize each gallery wrapper
    document.querySelectorAll('.article-gallery__wrapper').forEach(wrapper => {
      // Skip if already initialized
      if (wrapper.galleryInitialized) return;
      wrapper.galleryInitialized = true;
      
      const sliderComponent = wrapper.querySelector('.article-gallery-slider');
      const slider = sliderComponent?.querySelector('ul[role="list"]');
      const thumbnailsWrapper = wrapper.querySelector('.article-gallery__thumbnails-wrapper');
      const thumbnailsContainer = thumbnailsWrapper?.querySelector('.article-gallery__thumbnails');
      
      if (!slider || !thumbnailsContainer || !sliderComponent) return;
      
      const slides = Array.from(slider.querySelectorAll('.article-gallery__slide'));
      const thumbnails = Array.from(thumbnailsContainer.querySelectorAll('.article-gallery__thumbnail'));
      
      if (slides.length === 0 || thumbnails.length === 0) return;
      
      // Store references for cleanup
      wrapper._galleryHandlers = {
        sliderComponent,
        slider,
        thumbnailsContainer,
        thumbnails,
        slides,
        prevBtn: thumbnailsWrapper.querySelector('.article-gallery__thumbnails-nav--prev'),
        nextBtn: thumbnailsWrapper.querySelector('.article-gallery__thumbnails-nav--next')
      };
      
      let currentIndex = 0;

      // Helper: Update active thumbnail
      const updateActiveThumbnail = (activeIndex) => {
        thumbnails.forEach((thumb, index) => {
          if (index === activeIndex) {
            thumb.classList.add('article-gallery__thumbnail--active');
          } else {
            thumb.classList.remove('article-gallery__thumbnail--active');
          }
        });
      };

      const setCurrentIndex = (index) => {
        if (index < 0 || index >= slides.length) return;
        currentIndex = index;
        updateActiveThumbnail(currentIndex);
        const activeThumbnail = thumbnails[currentIndex];
        if (activeThumbnail) {
          requestAnimationFrame(() => {
            scrollThumbnailIntoView(activeThumbnail);
          });
        }
      };
      
      // Helper: Scroll thumbnail into view
      const scrollThumbnailIntoView = (thumbnail) => {
        if (!thumbnail) return;
        
        // Always scroll if there are more than 4 thumbnails
        if (thumbnails.length <= 4) return;
        
        // Wait for layout to settle
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const containerRect = thumbnailsContainer.getBoundingClientRect();
            const thumbnailRect = thumbnail.getBoundingClientRect();
            const containerTop = containerRect.top;
            const containerBottom = containerRect.bottom;
            const thumbnailTop = thumbnailRect.top;
            const thumbnailBottom = thumbnailRect.bottom;
            
            // Check if thumbnail is outside visible area
            const isAbove = thumbnailTop < containerTop;
            const isBelow = thumbnailBottom > containerBottom;
            
            if (isAbove || isBelow) {
              // Use the thumbnail's actual position relative to the container
              const thumbnailOffsetTop = thumbnail.offsetTop;
              const containerScrollTop = thumbnailsContainer.scrollTop;
              const containerHeight = thumbnailsContainer.clientHeight;
              const thumbnailHeight = thumbnail.offsetHeight || thumbnailRect.height;
              const gap = parseFloat(getComputedStyle(thumbnailsContainer).gap) || 12;
              
              let targetScrollTop;
              
              if (isBelow) {
                // Thumbnail is below visible area - scroll it into view
                // Position it so it's visible, ideally near the top
                targetScrollTop = thumbnailOffsetTop - gap;
              } else if (isAbove) {
                // Thumbnail is above visible area - scroll it into view
                // Position it at the top of visible area
                targetScrollTop = thumbnailOffsetTop - gap;
              } else {
                return; // Already visible
              }
              
              // Ensure we don't scroll beyond bounds
              const maxScroll = thumbnailsContainer.scrollHeight - containerHeight;
              targetScrollTop = Math.max(0, Math.min(targetScrollTop, maxScroll));
              
              thumbnailsContainer.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth'
              });
            }
          });
        });
      };
      
      const syncThumbnails = () => {
        if (!sliderComponent || typeof sliderComponent.currentPage !== 'number') return;

        const newIndex = sliderComponent.currentPage - 1;
        if (newIndex !== currentIndex) {
          setCurrentIndex(newIndex);
        }
      };

      // Thumbnail click handlers - navigate main slider
      thumbnails.forEach((thumbnail, index) => {
        const handler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const slide = slides[index];
          if (slide) {
            slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }

          setCurrentIndex(index);
        };
        
        thumbnail.addEventListener('click', handler);
        wrapper._galleryHandlers.thumbnailHandlers = wrapper._galleryHandlers.thumbnailHandlers || [];
        wrapper._galleryHandlers.thumbnailHandlers.push({ element: thumbnail, handler });
      });
      
      // Thumbnail navigation buttons
      const { prevBtn, nextBtn } = wrapper._galleryHandlers;
      
      if (thumbnails.length <= 4) {
        // Hide navigation if 4 or fewer thumbnails
        if (prevBtn) prevBtn.style.display = 'none';
        if (nextBtn) nextBtn.style.display = 'none';
      } else {
        // Show navigation buttons
        if (prevBtn) prevBtn.style.display = '';
        if (nextBtn) nextBtn.style.display = '';
        
        // Navigate main slider
        const navigateMainSlider = (direction) => {
          let targetIndex;
          
          if (direction === 'prev') {
            targetIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1;
          } else {
            targetIndex = (currentIndex + 1) % slides.length;
          }
          
          const targetSlide = slides[targetIndex];
          if (targetSlide) {
            targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
          
          setCurrentIndex(targetIndex);
        };
        
        const prevHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          navigateMainSlider('prev');
        };
        
        const nextHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          navigateMainSlider('next');
        };
        
        if (prevBtn) {
          prevBtn.addEventListener('click', prevHandler);
          wrapper._galleryHandlers.prevHandler = prevHandler;
        }
        
        if (nextBtn) {
          nextBtn.addEventListener('click', nextHandler);
          wrapper._galleryHandlers.nextHandler = nextHandler;
        }
      }
      
      if (sliderComponent && typeof sliderComponent.addEventListener === 'function') {
        sliderComponent.addEventListener('slideChanged', syncThumbnails);
        wrapper._galleryHandlers.slideChangedHandler = syncThumbnails;
      }
      
      // Initial thumbnail state
      setCurrentIndex(0);
    });
  }

  // Constrain thumbnails container to show exactly 4 thumbnails and match main image height
  function limitThumbnailsToFour() {
    document.querySelectorAll('.article-gallery__wrapper').forEach(wrapper => {
      const slider = wrapper.querySelector('.article-gallery-slider > ul');
      const thumbnailsWrapper = wrapper.querySelector('.article-gallery__thumbnails-wrapper');
      const thumbnailsContainer = thumbnailsWrapper?.querySelector('.article-gallery__thumbnails');
      
      if (!slider || !thumbnailsWrapper || !thumbnailsContainer) return;
      
      const thumbnails = Array.from(thumbnailsContainer.querySelectorAll('.article-gallery__thumbnail'));
      
      if (thumbnails.length <= 4) {
        thumbnailsContainer.style.removeProperty('--thumbnails-max-height');
        thumbnailsContainer.style.removeProperty('--thumbnail-height');
        return;
      }
      
      // Calculate height to match main image and fit 4 thumbnails
      const calculateHeights = () => {
        if (thumbnails.length === 0) return;
        
        // Get the main slider/image height
        const sliderHeight = slider.offsetHeight || slider.clientHeight;
        if (sliderHeight === 0) {
          // Retry if slider height not ready
          setTimeout(calculateHeights, 100);
          return;
        }
        
        // Set CSS custom properties for layout calculations
        thumbnailsWrapper.style.setProperty('--slider-height', `${sliderHeight}px`);
        
        // Get button heights and gaps
        const buttons = thumbnailsWrapper.querySelectorAll('.article-gallery__thumbnails-nav');
        const buttonHeight = buttons.length > 0 ? buttons[0].offsetHeight : 0;
        const wrapperGap = parseFloat(getComputedStyle(thumbnailsWrapper).gap) || 8;
        const totalButtonHeight = (buttonHeight * buttons.length) + (wrapperGap * (buttons.length > 0 ? buttons.length + 1 : 0));
        
        // Calculate available height for thumbnails (wrapper height minus buttons and gaps)
        const availableHeight = sliderHeight - totalButtonHeight;
        
        // Calculate thumbnail height to fit exactly 4 thumbnails
        const thumbnailGap = parseFloat(getComputedStyle(thumbnailsContainer).gap) || 12;
        const thumbnailHeight = (availableHeight - (thumbnailGap * 3)) / 4;
        
        // Set CSS custom properties instead of inline styles
        thumbnailsContainer.style.setProperty('--thumbnails-max-height', `${(thumbnailHeight * 4) + (thumbnailGap * 3)}px`);
        thumbnailsContainer.style.setProperty('--thumbnail-height', `${thumbnailHeight}px`);
      };
      
      // Ensure images are visible and load properly
      thumbnails.forEach(thumbnail => {
        const img = thumbnail.querySelector('img');
        if (img) {
          // Force image to load
          if (!img.complete) {
            img.loading = 'eager';
          }
          
          // Ensure image styles are correct
          if (img.style.display === 'none') {
            img.style.display = 'block';
          }
          img.style.visibility = 'visible';
          img.style.opacity = '1';
          
          // Set up load handlers
          img.addEventListener('load', calculateHeights, { once: true });
          img.addEventListener('error', () => {
            // Silently handle image load errors and recalculate heights
            calculateHeights();
          }, { once: true });
        }
      });
      
      // Wait for layout to settle, then calculate
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          calculateHeights();
        });
      });
      
      // Fallback timeout
      setTimeout(calculateHeights, 500);
    });
  }
  
  // Debounced resize handler
  let resizeTimeout;
  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      limitThumbnailsToFour();
    }, 150);
  };
  
  // Set up ResizeObserver to watch for slider height changes
  const setupResizeObserver = () => {
    document.querySelectorAll('.article-gallery__wrapper').forEach(wrapper => {
      const slider = wrapper.querySelector('.article-gallery-slider > ul');
      if (!slider) return;
      
      // Skip if already observed
      if (slider._thumbnailObserver) return;
      
      const observer = new ResizeObserver(() => {
        limitThumbnailsToFour();
      });
      observer.observe(slider);
      slider._thumbnailObserver = observer;
    });
  };
  
  // Initialize gallery thumbnails
  const initializeGallery = () => {
    initGalleryThumbnails();
    // Delay thumbnail height calculation to ensure images are loaded
    setTimeout(() => {
      limitThumbnailsToFour();
      setupResizeObserver();
    }, 100);
  };

  const initializeFancybox = () => {
    if (!window.jQuery || !jQuery.fancybox) return;

    jQuery(function($) {
      $('[data-fancybox^="article-gallery-"]').fancybox({
        loop: true,
        buttons: ['slideShow', 'thumbs', 'close'],
        clickContent: false,
        dblclickContent: false,
        fitToView: true
      });
    });
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeGallery();
      initializeFancybox();
    });
  } else {
    initializeGallery();
    initializeFancybox();
  }
  
  // Re-init on section load
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      // Reset initialization flags
      document.querySelectorAll('.article-gallery__wrapper').forEach(wrapper => {
        wrapper.galleryInitialized = false;
      });
      // Clean up old observers
      document.querySelectorAll('.article-gallery-slider > ul').forEach(slider => {
        if (slider._thumbnailObserver) {
          slider._thumbnailObserver.disconnect();
          slider._thumbnailObserver = null;
        }
      });
      initializeGallery();
      initializeFancybox();
    });
  }

  // Recalculate on window resize
  window.addEventListener('resize', handleResize);
  
  // Also recalculate when images load
  document.addEventListener('load', (e) => {
    if (e.target.tagName === 'IMG' && e.target.closest('.article-gallery__thumbnail')) {
      limitThumbnailsToFour();
    }
  }, true);

})();
